import {useCallback, useEffect, useRef, useState} from 'react'
import {useShowError} from '@hooks/useShowError/useShowError'
import {
    getDriverChatHistory,
    markDriverChatRead,
    pollDriverChat,
    sendDriverMessage,
} from '@containers/chat/api/api'
import {ChatMessage} from '@containers/chat/types'
import {playChatSound} from '@containers/chat/helpers/notificationSound'

const POLL_MS = 3000   // same interval as the legacy driver_chat.vm

const messageTime = (m: ChatMessage): number => {
    const t = typeof m.date === 'number' ? m.date : Date.parse(String(m.date))
    return Number.isNaN(t) ? 0 : t
}

/**
 * Driver chat thread: initial history plus INCREMENTAL polling (new messages and status
 * changes), sending with a message type, mark-as-read on open and a sound for incoming ones.
 * React Query is not used here because the thread state accumulates (merge, not replace).
 */
export const useDriverChat = (driverId: number, open: boolean) => {
    // useShowError() returns a NEW function on every render, so it is kept in a ref: putting it
    // into the effect deps caused an endless request loop (history/read on every render).
    const {onShowError} = useShowError()
    const onShowErrorRef = useRef(onShowError)
    onShowErrorRef.current = onShowError

    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)

    const sinceRef = useRef<number | undefined>()
    const statusSinceRef = useRef<number | undefined>()
    const lastIdRef = useRef<string | undefined>()

    // Duplicate-free merge keyed by serverId/id, falling back to clientMessageId.
    const merge = useCallback((incoming: ChatMessage[]) => {
        if (!incoming.length) {
            return
        }
        setMessages((prev) => {
            const byKey = new Map<string, ChatMessage>()
            const keyOf = (m: ChatMessage) => m.serverId || m.id || m.clientMessageId || `${m.date}-${m.message}`
            prev.forEach((m) => byKey.set(keyOf(m), m))
            incoming.forEach((m) => byKey.set(keyOf(m), {...byKey.get(keyOf(m)), ...m}))
            return Array.from(byKey.values()).sort((a, b) => messageTime(a) - messageTime(b))
        })
    }, [])

    // Initial load plus mark-as-read.
    useEffect(() => {
        if (!open || driverId <= 0) {
            return
        }
        let cancelled = false
        setLoading(true)
        getDriverChatHistory(driverId, 50, onShowErrorRef.current)
            .then((data) => {
                if (cancelled) {
                    return
                }
                const list = data ?? []
                setMessages(list)
                const last = list[list.length - 1]
                if (last) {
                    sinceRef.current = messageTime(last)
                    lastIdRef.current = last.serverId || last.id
                }
                statusSinceRef.current = Date.now()
            })
            .finally(() => !cancelled && setLoading(false))
        markDriverChatRead(driverId).catch(() => undefined)
        return () => {
            cancelled = true
        }
    }, [open, driverId])

    // Incremental polling while the thread is open.
    useEffect(() => {
        if (!open || driverId <= 0) {
            return
        }
        const tick = async () => {
            const res = await pollDriverChat(driverId, {
                since: sinceRef.current,
                lastIdHex: lastIdRef.current,
                statusSince: statusSinceRef.current,
            }).catch(() => null)
            if (!res) {
                return
            }
            const incoming = res.messages ?? []
            if (incoming.length) {
                merge(incoming)
                const last = incoming[incoming.length - 1]
                sinceRef.current = messageTime(last)
                lastIdRef.current = last.serverId || last.id
                // Sound only for incoming messages (from the driver), not for our own.
                if (incoming.some((m: ChatMessage) => String(m.sender).toUpperCase() === 'DRIVER')) {
                    playChatSound()
                    markDriverChatRead(driverId).catch(() => undefined)
                }
            }
            // Apply delivery/read status updates to messages already in the thread.
            const updates = res.statusUpdates ?? []
            if (updates.length) {
                setMessages((prev) =>
                    prev.map((m) => {
                        const upd = updates.find(
                            (u: {id?: string; serverId?: string}) => (u.serverId || u.id) === (m.serverId || m.id),
                        )
                        return upd?.status ? {...m, status: upd.status} : m
                    }),
                )
            }
            statusSinceRef.current = res.serverTime ?? Date.now()
        }
        const timer = window.setInterval(tick, POLL_MS)
        return () => window.clearInterval(timer)
    }, [open, driverId, merge])

    const send = useCallback(
        async (message: string, messageType: string) => {
            if (!message.trim() || driverId <= 0) {
                return
            }
            setSending(true)
            const clientMessageId = `${Date.now()}-${Math.random().toString(16).slice(2)}`
            // Show our own message optimistically, before the request completes.
            merge([{
                id: clientMessageId,
                clientMessageId,
                message,
                sender: 'DISPATCHER',
                messageType,
                date: Date.now(),
                status: 'SENT',
            }])
            try {
                await sendDriverMessage(driverId, {message, messageType, clientMessageId}, onShowErrorRef.current)
            } finally {
                setSending(false)
            }
        },
        [driverId, merge],
    )

    return {messages, loading, sending, send}
}